import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { furnitureService } from '../../services/furniture';
import { useAuth } from '../../contexts/AuthContext';
import { NEIGHBORHOODS } from '../../constants/neighborhoods';
import { imageUploadService, validateImageFile } from '../../services/imageUpload';

const MAX_LISTING_IMAGES = 5;

interface ListingImage {
  file: File;
  previewUrl: string;
}

const SellFurniture: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    condition: '',
    price: '',
    city: '',
    state: 'MA',
    zipCode: '',
    pickupDetails: '',
    neighborhood: '',
    wechatId: '',
  });
  const [selectedImages, setSelectedImages] = useState<ListingImage[]>([]);
  const previewUrls = useRef<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => () => {
    previewUrls.current.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    if (selectedImages.length + files.length > MAX_LISTING_IMAGES) {
      setErrorMessage(i18n.language.startsWith('zh')
        ? `最多上传 ${MAX_LISTING_IMAGES} 张图片。`
        : `You can upload up to ${MAX_LISTING_IMAGES} images.`);
      return;
    }

    try {
      files.forEach(validateImageFile);
    } catch (error) {
      setErrorMessage(t(error instanceof Error && error.message === 'image-too-large'
        ? 'furniture.sell.form.imageTooLarge'
        : 'furniture.sell.form.unsupportedImage'));
      return;
    }

    setErrorMessage(null);
    setSelectedImages((currentImages) => currentImages.concat(files.map((file) => {
      const previewUrl = URL.createObjectURL(file);
      previewUrls.current.push(previewUrl);
      return { file, previewUrl };
    })));
  };

  const handleRemoveImage = (previewUrl: string) => {
    URL.revokeObjectURL(previewUrl);
    previewUrls.current = previewUrls.current.filter((url) => url !== previewUrl);
    setSelectedImages((currentImages) => currentImages.filter((image) => image.previewUrl !== previewUrl));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    // Validate form
    if (!formData.title || !formData.description || !formData.category ||
        !formData.condition || !formData.price || !formData.city || !formData.neighborhood) {
      setErrorMessage(t('furniture.sell.form.required'));
      setLoading(false);
      return;
    }

    if (!agreedToTerms) {
      setErrorMessage(t('furniture.sell.form.agreementRequired'));
      setLoading(false);
      return;
    }

    try {
      const uploadedImages = selectedImages.length > 0
        ? await imageUploadService.uploadMultipleImages(selectedImages.map(({ file }) => file))
        : [];

      // Canonical payload (LAUNCH.md §2). Images optional in v1 (D2).
      const newFurniture = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        condition: formData.condition as any,
        price: parseFloat(formData.price),
        images: uploadedImages.map(({ url }) => url),
        location: {
          city: formData.city.trim(),
          state: formData.state.trim() || 'MA',
          ...(formData.zipCode.trim() ? { zipCode: formData.zipCode.trim() } : {}),
        },
        ...(formData.pickupDetails.trim()
          ? { pickupDetails: formData.pickupDetails.trim() }
          : {}),
        neighborhood: formData.neighborhood,
        wechatId: formData.wechatId.trim() || user?.wechatId,
      };

      await furnitureService.createFurniture(newFurniture);

      setSuccessMessage(t('furniture.sell.form.success'));

      setTimeout(() => {
        navigate('/seller/dashboard');
      }, 1500);
    } catch (error: any) {
      console.error('Error creating furniture:', error);
      setErrorMessage(error.response?.data?.message || t('furniture.sell.form.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <div className="px-4 sm:px-0">
            <h3 className="text-lg font-medium leading-6 text-gray-900">{t('furniture.sell.title')}</h3>
            <p className="mt-1 text-sm text-gray-600">
              {t('furniture.sell.subtitle')}
            </p>
            <p className="mt-4 text-sm text-gray-600">
              {t('furniture.sell.form.tips')}
            </p>
            <ul className="mt-2 text-sm text-gray-600 list-disc pl-5 space-y-1">
              <li>{t('furniture.sell.form.tipPhotos')}</li>
              <li>{t('furniture.sell.form.tipCondition')}</li>
              <li>{t('furniture.sell.form.tipDetails')}</li>
              <li>{t('furniture.sell.form.tipPrice')}</li>
            </ul>
          </div>
        </div>

        <div className="mt-5 md:mt-0 md:col-span-2">
          {successMessage && (
            <div className="rounded-md bg-green-50 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">{successMessage}</p>
                </div>
              </div>
            </div>
          )}

          {errorMessage && (
            <div className="rounded-md bg-red-50 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{errorMessage}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="shadow sm:rounded-md sm:overflow-hidden">
              <div className="px-4 py-5 bg-white space-y-6 sm:p-6">
                {/* Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                    {t('furniture.sell.form.title')} *
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="title"
                      id="title"
                      value={formData.title}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder={t('furniture.sell.form.titlePlaceholder')}
                      required
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    {t('furniture.sell.form.description')} *
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="description"
                      name="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder={t('furniture.sell.form.descriptionPlaceholder')}
                      required
                    />
                  </div>
                </div>

                {/* Category and Condition */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                      {t('furniture.sell.form.category')} *
                    </label>
                    <div className="mt-1">
                      <select
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        required
                      >
                        <option value="">{t('furniture.sell.form.selectCategory')}</option>
                        <option value="sofa">{t('furniture.sell.form.sofa')}</option>
                        <option value="table">{t('furniture.sell.form.table')}</option>
                        <option value="chair">{t('furniture.sell.form.chair')}</option>
                        <option value="desk">{t('furniture.sell.form.desk')}</option>
                        <option value="bed">{t('furniture.sell.form.bed')}</option>
                        <option value="dresser">{t('furniture.sell.form.dresser')}</option>
                        <option value="storage">{t('furniture.sell.form.storage')}</option>
                        <option value="lighting">{t('furniture.sell.form.lighting')}</option>
                        <option value="other">{t('furniture.sell.form.other')}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="condition" className="block text-sm font-medium text-gray-700">
                      {t('furniture.sell.form.condition')} *
                    </label>
                    <div className="mt-1">
                      <select
                        id="condition"
                        name="condition"
                        value={formData.condition}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        required
                      >
                        <option value="">{t('furniture.sell.form.selectCondition')}</option>
                        <option value="like-new">{t('furniture.sell.form.likeNew')}</option>
                        <option value="good">{t('furniture.sell.form.good')}</option>
                        <option value="fair">{t('furniture.sell.form.fair')}</option>
                        <option value="needs-repair">{t('furniture.sell.form.needsRepair')}</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Price and Location */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                      {t('furniture.sell.form.price')} *
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        name="price"
                        id="price"
                        value={formData.price}
                        onChange={handleChange}
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                        placeholder="0.00"
                        min="1"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                      {t('furniture.sell.form.city')} *
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="city"
                        id="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder={t('furniture.sell.form.cityPlaceholder')}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* State and Zip */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                      {t('furniture.sell.form.state')}
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="state"
                        id="state"
                        value={formData.state}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder="MA"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">
                      {t('furniture.sell.form.zip')}
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="zipCode"
                        id="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder={t('furniture.sell.form.zipPlaceholder')}
                      />
                    </div>
                  </div>
                </div>

                {/* Pickup details */}
                <div>
                  <label htmlFor="pickupDetails" className="block text-sm font-medium text-gray-700">
                    {t('furniture.sell.form.pickup')}
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="pickupDetails"
                      name="pickupDetails"
                      rows={2}
                      value={formData.pickupDetails}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder={t('furniture.sell.form.pickupPlaceholder')}
                    />
                  </div>
                </div>

                {/* Neighborhood and WeChat */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="neighborhood" className="block text-sm font-medium text-gray-700">
                      {t('furniture.sell.form.neighborhood')} *
                    </label>
                    <div className="mt-1">
                      <select
                        id="neighborhood"
                        name="neighborhood"
                        value={formData.neighborhood}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        required
                      >
                        <option value="">{t('furniture.listing.allNeighborhoods')}</option>
                        {NEIGHBORHOODS.map((item) => (
                          <option key={item.slug} value={item.slug}>
                            {i18n.language.startsWith('zh') ? item.zh : item.en}
                          </option>
                        ))}
                      </select>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">{t('furniture.sell.form.neighborhoodHelp')}</p>
                  </div>
                  <div>
                    <label htmlFor="wechatId" className="block text-sm font-medium text-gray-700">
                      {t('furniture.sell.form.wechat')}
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="wechatId"
                        id="wechatId"
                        value={formData.wechatId}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder={t('furniture.sell.form.wechat')}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">{t('furniture.sell.form.wechatHelp')}</p>
                  </div>
                </div>

                {/* Photo upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('furniture.sell.form.photo')}
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      {selectedImages.length > 0 ? (
                        <div className="mb-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                          {selectedImages.map(({ previewUrl }, index) => (
                            <div key={previewUrl} className="relative">
                              <img src={previewUrl} alt={`Preview ${index + 1}`} className="h-32 w-full object-cover rounded-md" />
                              <button
                                type="button"
                                aria-label={`${t('common.delete')} ${index + 1}`}
                                onClick={() => handleRemoveImage(previewUrl)}
                                className="mt-1 text-xs text-red-600 hover:text-red-800 underline"
                              >
                                {t('common.delete')}
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                          aria-hidden="true"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="image"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                        >
                          <span>{t('furniture.sell.form.upload')}</span>
                          <input
                            id="image"
                            name="image"
                            type="file"
                            accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                            multiple
                            onChange={handleImageChange}
                            className="sr-only"
                          />
                        </label>
                        <p className="pl-1">{t('furniture.sell.form.drag')}</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        {t('furniture.sell.form.imageTypes')} · {selectedImages.length}/{MAX_LISTING_IMAGES}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 bg-gray-50 sm:px-6 space-y-4">
                <label className="flex items-start text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="ml-2">
                    {t('furniture.sell.form.agreementPrefix')}{' '}
                    <Link to="/terms" className="text-indigo-600 underline" target="_blank" rel="noreferrer">
                      {t('furniture.sell.form.agreementTerms')}
                    </Link>{' '}
                    {t('furniture.sell.form.agreementAnd')}{' '}
                    <Link to="/disclaimer" className="text-indigo-600 underline" target="_blank" rel="noreferrer">
                      {t('furniture.sell.form.agreementDisclaimer')}
                    </Link>
                  </span>
                </label>
                <div className="text-right">
                <button
                  type="submit"
                  disabled={loading || !agreedToTerms}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {t(loading ? 'furniture.sell.form.submitting' : 'furniture.sell.form.submit')}
                </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SellFurniture;
